import { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { LanguageContext } from './LanguageContext';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import Header from './Header';
import Footer from './Footer';

function Home() {

    const URL = process.env.REACT_APP_API_URL;
    const { language, textContent } = useContext(LanguageContext);  
    const [status, setStatus] = useState('');
    

    const handleSubmit = async (e) => {
        e.preventDefault();
        const name = e.target.name.value;
        const email = e.target.email.value;
        const message = e.target.message.value;

        try {
        const response = await axios.post(URL + '/contact', {
            name,
            email,
            message,
        });

        if (response.data.success) {
            setStatus(textContent.messageSent);
            // Track GA event when message sent successfully
            trackEvent('contact_form_submitted', {
                name: name,
                email: email,
            });
            e.target.reset();
        } else {
            setStatus(textContent.messageFailed);
        }
        } catch (error) {        
        setStatus(textContent.errorOccurred);
        }
    };

    
    const location = useLocation();
    useEffect(() => {
        if (location.hash) {
        const el = document.querySelector(location.hash);
        if (el) {
            el.scrollIntoView({  });
        }
        }
    }, [location]);

    // Auto-hide status message after 3 seconds
    useEffect(() => {
        if (status) {
            const timer = setTimeout(() => {
                setStatus('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [status]);

    return (
        <div className="bg-gray-100 min-h-screen">
            <Header />
        
            <main className="container mx-auto px-4 py-8 max-w-none w-full">
                <section id="hero" className="text-center py-16 bg-white shadow-md rounded-lg px-4 sm:px-8 scroll-mt-32">
                    <h2 className="text-4xl font-bold mb-4">{textContent.heroTitle}</h2>
                    <p className="text-gray-700 mb-6">{textContent.heroDescription}</p>
                    <a href="#services" className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800">
                        {textContent.exploreServices}
                    </a>
                </section>

                <section id="about" className="py-8 bg-gray-200 rounded-lg px-4 sm:px-8 scroll-mt-32 mt-8">                
                    <h3 className="text-3xl font-bold mb-4 text-center sm:text-left">{textContent.aboutTitle}</h3>
                    
                    <div className="bg-white p-6 rounded-lg shadow-md mb-8 mt-8">
                        <h4 className="text-xl font-bold mb-2">{language === 'th' ? 'วิสัยทัศน์' : 'Vision'}</h4>
                        <p className="text-gray-700">
                            {language === 'th'
                                ? 'มุ่งมั่นเพื่อป็นผู้นำด้านปัญญาประดิษฐ์และเทคโนโลยีสารสนเทศ ในการยกระดับดับคุณภาพชีวิตและการขับเคลื่อนเศรษฐกิจดิจิทัลของประเทศไทย'
                                : 'To be a leader in artificial intelligence and information technology, enhancing quality of life and driving Thailand’s digital economy.'}
                        </p>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                        <h4 className="text-xl font-bold mb-2">{language === 'th' ? 'พันธกิจ' : 'Mission'}</h4>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            {language === 'th' ? (
                                <>
                                    <li>พัฒนาและส่งมอบโซลูชันด้านปัญญาประดิษฐ์และเทคโนโลยีสารสนเทศที่ตอบโจทย์ลูกค้า</li>                            
                                    <li>ปรับปรุงกระบวนการทำงานภายในให้มีประสิทธิภาพอยู่เสมอ</li>
                                    <li>พัฒนาบุคลากรให้มีความเชี่ยวชาญระดับสูงและพร้อมที่จะแลกเปลี่ยนความรู้</li>
                                    <li>สร้างความสัมพันธ์ที่ยั่งยืนกับลูกค้า บนพื้นฐานของความไว้วางใจและความพึงพอใจสูงสุด</li>
                                    <li>สร้างเครือข่ายความร่วมมือ เพื่อขับเคลื่อนระบบนิเวศเทคโนโลยีดิจิทัลของประเทศอย่างยั่งยืน</li>                                    
                                </>
                            ) : (
                                <>
                                    <li>Develop and deliver AI and IT solutions that meet customer needs.</li>
                                    <li>Continuously improve internal processes to maintain efficiency.</li>
                                    <li>Develop highly skilled personnel who are ready to exchange knowledge.</li>
                                    <li>Build sustainable relationships with customers based on trust and utmost satisfaction.</li>
                                    <li>Create collaborative networks to drive a sustainable digital technology ecosystem for the country.</li>
                                </>
                            )}
                        </ul>
                    </div>                        
                    <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                        <h4 className="text-xl font-bold mb-4">{language === 'th' ? 'ค่านิยม' : 'Core Values'}</h4>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            {language === 'th' ? (
                                <>
                                    <li><strong>A</strong>gility (ความคล่องตัว) : ปรับตัวต่อการเปลี่ยนแปลงได้อย่างรวดเร็ว</li>
                                    <li><strong>I</strong>nnovation (นวัตกรรม) : พัฒนาโซลูชันด้วยนวัตกรรม</li>
                                    <li><strong>B</strong>rilliance (ความเป็นเลิศ) : มุ่งมั่นสู่ความเป็นเลิศขององค์กร</li>
                                    <li><strong>I</strong>ntegrity (ความซื่อสัตย์) : ยึดมั่นด้วยจริยธรรมและความซื่อสัตย์</li>
                                    <li><strong>Z</strong>eal (ความกระตือรือร้น) : ปฏิบัติงานด้วยความมุ่งมั่น</li>
                                </>
                            ) : (
                                <>
                                    <li><strong>A</strong>gility:   Quickly adapting to change.</li>
                                    <li><strong>I</strong>nnovation : Develop solutions with innovation.</li>
                                    <li><strong>B</strong>rilliance : Commit to organizational excellence.</li>
                                    <li><strong>I</strong>ntegrity : Uphold ethics and integrity.</li>
                                    <li><strong>Z</strong>eal : Perform with dedication.</li>
                                </> 
                            )}
                        </ul>
                    </div>
                </section>

                <section id="services" className="py-8 bg-gray-200 rounded-lg px-4 sm:px-8 scroll-mt-32 mt-8">
                    <h3 className="text-3xl font-bold mb-4 text-center sm:text-left">{textContent.servicesTitle}</h3>
                    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
                            {language === 'th' ? (
                                <>
                                    <li className="bg-white p-4 rounded-lg shadow-md">
                                        <h4 className="text-xl font-bold">พัฒนา AI</h4>
                                        <p>สร้างระบบเอไอที่ตอบโจทย์ธุรกิจของคุณโดยเฉพาะ</p>
                                    </li> 
                                    <li className="bg-white p-4 rounded-lg shadow-md">
                                        <h4 className="text-xl font-bold">พัฒนา Software</h4>
                                        <p>พัฒนาซอฟต์แวร์ที่ตรงตามความต้องการของธุรกิจคุณ</p>
                                    </li>
                                    <li className="bg-white p-4 rounded-lg shadow-md">
                                        <h4 className="text-xl font-bold">ให้คำปรึกษาด้าน IT</h4>
                                        <p>ให้คำปรึกษาอย่างเชี่ยวชาญเพื่อผสาน IT เข้ากับกระบวนการทางธุรกิจ</p>
                                    </li>
                                    <li className="bg-white p-4 rounded-lg shadow-md">                                
                                        <h4 className="text-xl font-bold">อบรมด้าน IT</h4>
                                        <p>อบรมและเวิร์กชอปเพื่อเพิ่มทักษะด้าน IT ให้กับทีมของคุณ</p> 
                                        <a href="/course" className="mt-2 inline-block text-blue-600 hover:underline">
                                            ดูเพิ่มเติม
                                        </a>                               
                                    </li>

                                </>
                            ) : (
                                <>                                                                    
                                    <li className="bg-white p-4 rounded-lg shadow-md">
                                        <h4 className="text-xl font-bold">AI Development</h4>
                                        <p>Develop AI systems tailored to your specific business needs.</p>
                                    </li> 
                                    <li className="bg-white p-4 rounded-lg shadow-md">
                                        <h4 className="text-xl font-bold">Software Development</h4>
                                        <p>Create software solutions that align with your business requirements.</p>
                                    </li>
                                    <li className="bg-white p-4 rounded-lg shadow-md">
                                        <h4 className="text-xl font-bold">IT Consulting</h4>
                                        <p>Provide expert consulting to integrate IT into your business processes.</p>
                                    </li>
                                    <li className="bg-white p-4 rounded-lg shadow-md">
                                        <h4 className="text-xl font-bold">IT Training</h4>
                                        <p>Offer training and workshops to enhance your team’s IT skills.</p>
                                        <a href="/course" className="mt-2 inline-block text-blue-600 hover:underline">
                                            See More
                                        </a> 
                                    </li>
                                </> 
                            )}                        

                    </ul>
                </section>

                <section id="contact" className="py-16 px-4 sm:px-8 scroll-mt-16">
                    <h3 className="text-3xl font-bold mb-4 text-center sm:text-left">
                        {textContent.contactTitle}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h4 className="text-xl font-bold mb-4">{textContent.address}</h4>
                            <p className="text-gray-700">
                                {textContent.companyName}<br />
                                {textContent.companyAddress}<br />
                                {textContent.companyDistrict}<br />
                                {textContent.companyCity}
                            </p>
                            <div className="mt-4">
                                <p className="flex items-center text-gray-700">
                                    <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                                    info@aibusiness.co.th
                                </p>
                                <p className="flex items-center text-gray-700 mt-2">
                                    <FontAwesomeIcon icon={faPhone} className="mr-2" />
                                    061-253-0020
                                </p>
                                <a href="#map" className="flex items-center text-gray-700 mt-2 hover:underline">
                                    <FontAwesomeIcon icon={faLocationDot} className="mr-2" />
                                    {textContent.map}
                                </a>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md">
                            <div className="mb-4">
                                <label htmlFor="name" className="block text-gray-700 font-bold mb-2">
                                    {textContent.name}
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    maxLength={100}
                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    placeholder="Your Name"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label htmlFor="email" className="block text-gray-700 font-bold mb-2">
                                    {textContent.email}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    maxLength={100}
                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    placeholder="Your Email"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label htmlFor="message" className="block text-gray-700 font-bold mb-2">
                                    {textContent.message}
                                </label>
                                <textarea
                                    id="message"
                                    maxLength={500}
                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    rows="4"
                                    placeholder="Your Message"
                                    required
                                ></textarea>
                            </div>
                            <button
                                type="submit"
                                className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800"
                            >
                                {textContent.sendMessage}
                            </button>
                        </form>
                    </div>
                    {status && <p className="text-center text-green-600 mt-4">{status}</p>}

                    <div id="map" className="mt-8 scroll-mt-32">
                        <h4 className="text-xl font-bold mb-4 text-center sm:text-left">
                            {textContent.location}
                        </h4>
                        <div className="rounded-lg overflow-hidden shadow-md">
                            <iframe
                                title="Google Map"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3874.022693373607!2d100.53292749072057!3d13.806408503060881!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDQ4JzIzLjEiTiAxMDDCsDMxJzU4LjUiRQ!5e0!3m2!1sen!2sth!4v1680000000000!5m2!1sen!2sth"
                                width="100%"
                                height="400"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            ></iframe>
                        </div>
                    </div>
                </section>
            </main>


            <Footer />
        </div>
    );
}

export default Home;